// 스테이지 보상 미리보기·첫 클리어 보상의 순수 로직(정본: docs/STAGE-WORLD-PRD.md 3.1·3.2절). DOM·저장소 없음.
// 값의 출처: 클리어 배율·첫 클리어·스페셜 확률 = special.ts, 지역 클리어/카드 골드 = regions.ts, 드롭 표 = world.ts DROP_TABLES,
// 강화석 배율·등급 = economy.ts, 보석 = consumables.ts, EXP = growth.ts. 이 파일은 그 값을 조합만 한다(수치를 새로 만들지 않는다).
import { REGION_IDS, TOWN_COUNT } from '../levels';
import { REGIONS } from '../regions';
import { XP_BOSS, XP_CLEAR, XP_ELITE_MULT, XP_KILL, XP_REGION_MULT, stageXpMult } from '../growth';
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT, STONE_TIERS, stoneTierForIlvl, type StoneTier } from './economy';
import { GEM_DROP, gemDropTier } from './consumables';
import { CLEAR_REWARD_MULT, FIRST_CLEAR, specialChance, stageKind, type DifficultyKey, type FirstClearDef, type StageKind } from './special';
import { stagePlan } from './stages';
import { DIFFICULTY, DROP_TABLES, REGIONS_90D, stageById } from './world';
import type { Grade, RegionNo, StageId } from './types';

export interface Range { readonly min: number; readonly max: number }

/** 스테이지 id의 종류(일반·중간보스·지역보스) */
function kindOf(id: StageId): StageKind {
  const s = stageById(id);
  return s ? stageKind(s.index) : 'normal';
}
function regionNoOf(id: StageId): RegionNo {
  return Number(id.slice(1, 2)) as RegionNo;
}

/** 클리어 골드·카드에 곱하는 배율. 스테이지 판이 아니면(자유 입장 등) 1(배율 없음) */
export function clearMult(id: StageId | null): number {
  return id ? CLEAR_REWARD_MULT[kindOf(id)] : 1;
}

// ── 반복 보상(입장 카드 '반복 보상') ───────────────────
export interface RepeatReward {
  /** 클리어 골드·카드 배율(일반 0.95·n-5 2·n-10 3.5, 하드·헬 골드 배율은 별도) */
  readonly mult: number;
  /** 이 스테이지 판의 처치 수(보스 방 제외 웨이브 합) */
  readonly kills: number;
  /** 클리어 골드 + 처치 골드 + 카드 1장 골드 범위(장비 골드 옵션 제외). 배율은 클리어 base·카드에만 곱한다 */
  readonly gold: Range;
  /** 클리어 EXP 범위(엘리트가 모두 일반이면 min, 모두 엘리트면 max에 가깝게 더한다, 레벨 차 보정 제외) */
  readonly exp: Range;
  /** 강화석 개수 범위(기대값 ±1σ)와 기대값, 강화석 등급 */
  readonly stones: Range;
  readonly stonesExpected: number;
  readonly stoneTier: StoneTier;
}

/** 이 스테이지 판의 처치 수와 엘리트 수(웨이브 데이터 그대로) */
function waveTotals(id: StageId): { kills: number; elites: number; byKind: number[] } {
  const plan = stagePlan(id);
  const byKind = [0, 0, 0, 0];
  let elites = 0;
  for (const room of plan.rooms) for (const w of room.waves) {
    for (let k = 0; k < 4; k++) byKind[k] += w.counts[k] ?? 0;
    elites += w.elites ?? 0;
  }
  return { kills: byKind.reduce((a, b) => a + b, 0), elites, byKind };
}

export function stoneTierName(tier: StoneTier): string {
  return STONE_TIERS[tier].name;
}

/** 스테이지 id·난이도의 반복 보상 범위(입장 카드용). 난이도는 골드·강화석·EXP 배율을 곱한다 */
export function repeatReward(id: StageId, diff: DifficultyKey = 'normal'): RepeatReward {
  const no = regionNoOf(id);
  const rid = REGION_IDS[no - 1];
  const info = REGIONS[rid];
  const kind = kindOf(id);
  const mult = CLEAR_REWARD_MULT[kind];
  const { kills, elites, byKind } = waveTotals(id);
  const gm = DIFFICULTY_LOOT[diff].gold;
  const rw = info.reward;
  // 배율은 클리어 base와 카드에만 곱한다(처치당 골드는 그대로, special.ts CLEAR_REWARD_MULT)
  const killGold = kills * rw.perKill;
  const gold: Range = {
    min: Math.round((rw.base * mult + killGold + rw.cardMin * mult) * gm),
    max: Math.round((rw.base * mult + killGold + rw.cardMax * mult) * gm),
  };
  const stage = stageById(id);
  const sx = stageXpMult(stage ? stage.index : 1);
  const diffXp = DIFFICULTY[diff].xp;
  let killXp = 0;
  for (let k = 0; k < 4; k++) killXp += byKind[k] * XP_KILL[k];
  const avgKillXp = kills > 0 ? killXp / kills : 0;
  const region = XP_REGION_MULT[rid];
  // 클리어 EXP + 보스 처치 EXP(일반 스테이지는 클리어에 함께 준다, flow.stageClear)
  const fixed = XP_CLEAR[rid] + XP_BOSS[rid];
  const base = killXp * region * sx * diffXp + fixed;
  const eliteExtra = elites * avgKillXp * (XP_ELITE_MULT - 1) * region * sx * diffXp;
  const exp: Range = { min: Math.round(base), max: Math.round(base + eliteExtra) };
  // 강화석: 처치 1회마다 stoneChance(표) × 난이도 배율. 보스 방은 보스 처치 1회가 더 굴린다
  const table = DROP_TABLES[`drop_${kind}`];
  const rolls = kills + (kind === 'normal' ? 0 : 1);
  const p = table.stoneChance, m = DIFFICULTY_LOOT[diff].stones;
  // 분해 대체 강화석(salvageStone, 1개·난이도 배율 없음)도 같은 굴림에서 나온다(gear.rollKillDrops)
  const expected = rolls * (p * m + table.salvageStone);
  const sigma = Math.sqrt(rolls * p * (1 - p)) * m;
  const stones: Range = { min: Math.max(0, Math.floor(expected - sigma)), max: Math.ceil(expected + sigma) };
  const ilvl = REGIONS_90D[no - 1].levels[0] + DIFFICULTY_ILVL_ADD[diff];
  return { mult, kills, gold, exp, stones, stonesExpected: Math.round(expected * 10) / 10, stoneTier: stoneTierForIlvl(ilvl) };
}

// ── 확정 드롭(입장 카드 '확정 드롭') ───────────────────
export interface BossDrops {
  /** 보스 확정 장비 수(보스 방이 없는 일반 스테이지는 0) */
  readonly equips: number;
  /** 확정 장비가 영웅 이상일 확률(0..1), 그중 전설 확률 */
  readonly epicPlus: number;
  readonly legend: number;
  /** 보석 확정 개수(지역3 이상) */
  readonly gems: number;
}
export function bossDrops(id: StageId, diff: DifficultyKey = 'normal'): BossDrops {
  const kind = kindOf(id);
  if (kind === 'normal') return { equips: 0, epicPlus: 0, legend: 0, gems: 0 };
  const t = DROP_TABLES[`drop_${kind}`];
  const w = t.bossGradeWeights;
  const sum = (Object.values(w) as number[]).reduce((a, b) => a + b, 0);
  const frac = (gs: Grade[]) => gs.reduce((a, g) => a + w[g], 0) / sum;
  const gemTier = gemDropTier(regionNoOf(id), diff);
  return {
    equips: Math.max(1, t.guaranteed),
    epicPlus: frac(['epic', 'set', 'legend']),
    legend: frac(['legend']),
    gems: gemTier > 0 ? (kind === 'final' ? GEM_DROP.boss.final : GEM_DROP.boss.mid) : 0,
  };
}

// ── 첫 클리어 보상 ────────────────────────────────────
export interface FirstClearReward {
  /** 골드 = 지역 클리어 base × 표의 배수. 난이도 골드 배율은 곱하지 않는다(1회성 보상, docs/ECONOMY.md 13절) */
  readonly gold: number;
  readonly stones: number;
  readonly stoneTier: StoneTier;
  /** 확정 장비 최소 등급(없으면 null) */
  readonly gearMinRarity: FirstClearDef['gearMinRarity'];
  /** 세트 조각 수. 세트 조각 화폐가 아직 없어 세트 등급 장비 1개로 지급한다(firstClearGearPlan) */
  readonly setFragments: number;
  /** n-10 첫 클리어는 다음 마을로 이동한다 */
  readonly travel: boolean;
}
export function firstClearReward(id: StageId, diff: DifficultyKey = 'normal'): FirstClearReward {
  const no = regionNoOf(id);
  const def = FIRST_CLEAR[kindOf(id)];
  const base = REGIONS[REGION_IDS[no - 1]].reward.base;
  const ilvl = REGIONS_90D[no - 1].levels[0] + DIFFICULTY_ILVL_ADD[diff];
  return {
    gold: Math.round(base * def.gold),
    stones: def.stones,
    stoneTier: stoneTierForIlvl(ilvl),
    gearMinRarity: def.gearMinRarity,
    setFragments: def.setFragments,
    // 다음 마을 지도가 실제로 있을 때만(TOWN_COUNT, 3개월 C2부터 마을⑥까지) — 기획표(REGIONS_90D)가 아니라 실제 지도 수로 센다
    travel: kindOf(id) === 'final' && no < TOWN_COUNT,
  };
}

/** 첫 클리어로 받는 장비 목록의 등급 하한. 희귀 이상 1개, 영웅 이상 1개(+ 세트 조각 → 세트 1개) */
export interface GearPlanEntry { readonly minGrade: Grade; readonly maxGrade: Grade; readonly label: string }
/**
 * 첫 클리어 장비 굴림 계획. 전설은 보스 확정 드롭에서만 나오게 상한을 세트로 둔다(c009 전설 희소성 유지 — 첫 클리어는 스테이지당 1회라
 * 시뮬 전설 기대값에 더해지지 않게). 세트 조각 화폐가 없으므로 세트 조각 1 = 세트 등급 장비 1개.
 */
export function firstClearGearPlan(r: FirstClearReward): GearPlanEntry[] {
  const out: GearPlanEntry[] = [];
  if (r.gearMinRarity) out.push({ minGrade: r.gearMinRarity, maxGrade: 'set', label: r.gearMinRarity === 'rare' ? '희귀 이상 장비' : '영웅 이상 장비' });
  for (let i = 0; i < r.setFragments; i++) out.push({ minGrade: 'set', maxGrade: 'set', label: '세트 장비(조각)' });
  return out;
}

/** 이 스테이지·난이도에서 한 판 스페셜 몬스터가 나올 확률(0..1). 보스판은 0 */
export function specialChanceFor(id: StageId, diff: DifficultyKey = 'normal'): number {
  const s = stageById(id);
  return s ? specialChance(s.index, diff) : 0;
}
